import { Injectable } from '@angular/core';
import { EntityActionOptions, EntityCollectionServiceBase, EntityCollectionServiceElementsFactory, MergeStrategy } from '@ngrx/data';
import { Shipment } from '../models/shipment';
import { Quote } from '../models/quote';
import { debounceTime, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { emptyShipment } from '../constants/empty-shipment';
import { combineLatest, Observable } from 'rxjs';
import { ShipmentStatusEnum } from '../enum/shipment-status.enum';
import { Clock } from '../utils/clock';
import { Store } from '@ngrx/store';
import { AppState } from '../store';
import { selectSetting } from '../store/profile/profile.state';
import { SettingEnum } from '../enum/setting.enum';
import { Card } from '../models/card';
import { emptyCard } from '../constants/empty-card';
import { selectShipmentEditorState } from '../store/shipment-editor/shipment-editor.state';
import { ShipmentState as ShipmentStateUtil } from '../utils/shipment-state';
import { shipmentHash } from '../utils/shipment-hash';
import { shipmentEditorChangeDetected } from '../store/shipment-editor/shipment-editor.actions';
import { ActivatedRouteSnapshot, MaybeAsync, RedirectCommand, Resolve, RouterStateSnapshot } from '@angular/router';
import { ActionService } from '../modules/shared/services/action.service';
import { ActionEnum } from '../enum/action.enum';
import { ActionStatusEnum } from '../enum/action-status.enum';
import { ShipmentBoxPackResult } from '../models/shipment-box-pack-result';

@Injectable({ providedIn: 'root' })
export class ShipmentService extends EntityCollectionServiceBase<Shipment> implements Resolve<Observable<Shipment>> {

    constructor(serviceElementsFactory: EntityCollectionServiceElementsFactory, private state: Store<AppState>,
                private actionService: ActionService) {
        super('Shipment', serviceElementsFactory);
    }

    resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): MaybeAsync<Observable<Shipment> | RedirectCommand> {
        return this.getByKey(route.paramMap.get('id'));
    }

    getByKey(key: any, options?: EntityActionOptions): Observable<Shipment> {
        return super.getByKey(key, options);
    }

    cacheQuotes(id: string, quotes: Quote[], preSelectedQuoteId?: string): void {
        this.entityMap$.pipe(
            map(entities => entities[id]),
            take(1)
        ).subscribe(shipment => {
            if (shipment) {
                const updatedShipment = { ...shipment, quotes, preSelectedQuoteId };
                this.updateOneInCache(updatedShipment, { mergeStrategy: MergeStrategy.OverwriteChanges });
            }
        });
    }

    create(): Observable<Shipment> {
        const shipment = { ...emptyShipment, id: null, quote: null };
        const defaultShipper$: Observable<Card> = this.state.select(selectSetting(SettingEnum.DEFAULT_SHIPPER));
        const defaultDestination$: Observable<Card> = this.state.select(selectSetting(SettingEnum.DEFAULT_DESTINATION));
        const defaultCurrency$: Observable<string> = this.state.select(selectSetting(SettingEnum.DEFAULT_CURRENCY));
        const defaultUnits$: Observable<string> = this.state.select(selectSetting(SettingEnum.DEFAULT_UNITS));
        return combineLatest([ defaultShipper$, defaultDestination$, defaultCurrency$, defaultUnits$ ]).pipe(
            map(([ shipper, destination, currency, units ]) => ({
                ...emptyShipment,
                id: null,
                shipper: { ...emptyCard, ...shipper },
                destination: { ...emptyCard, ...destination },
                currency,
                units,
                quote: null
            })),
            switchMap((shipment) => this.add(shipment))
        );
    }

    duplicate(source: Shipment): Observable<Shipment> {
        const shipment = {
            ...source,
            id: null,
            quote: null,
            quotes: [],
            documents: [],
            username: null,
        };
        return this.add(shipment);
    }

    return(source: Shipment): Observable<Shipment> {
        const shipment = {
            ...source,
            shipper: { ...source.destination },
            destination: { ...source.shipper},
            id: null,
            quote: null,
            quotes: [],
            documents: [],
            username: null,
        };
        return this.add(shipment);
    }

    observe(id: string): Observable<Shipment> {
        return this.entityMap$.pipe(
            map(entities => entities[id])
        );
    }

    observeStatus(id: string): Observable<ShipmentStatusEnum> {
        return this.observe(id).pipe(
            filter(shipment => !!shipment),
            map(shipment => shipment.statusCode)
        );
    }

    observeValidQuoteCount(id: string): Observable<number> {
        return this.observe(id).pipe(
            map(shipment => shipment.quotes),
            map(quotes => quotes.filter((q) => q.expiry > Clock.now())),
            map(quotes => quotes.length)
        );
    }

    changePreSelectedQuote(shipmentId: string, quoteId: string): Observable<any> {
        return this.update({ id: shipmentId, preSelectedQuoteId: quoteId });
    }

    canVoid(shipment: Shipment): boolean {
        return shipment.statusCode === ShipmentStatusEnum.MANIFESTED;
    }

    boxPack(ids: string[]): Observable<Record<string, ShipmentBoxPackResult>> {
        return this.actionService.sync(ActionEnum.SHIPMENT_BOX_PACK, ids).pipe(
            filter(action => action.status === ActionStatusEnum.COMPLETED || action.status === ActionStatusEnum.ERROR),
            map(action => {
                const output = (action.output ?? {}) as Record<string, ShipmentBoxPackResult>;
                if (action.status === ActionStatusEnum.COMPLETED) {
                    return output;
                }

                return ids.reduce((results, id) => ({
                    ...results,
                    [id]: output[id] ?? {
                        success: false,
                        error: $localize`Box packing could not be completed. Please try again.`
                    }
                }), {});
            })
        );
    }
}
